import { utcToZonedTime, zonedTimeToUtc, format } from 'date-fns-tz';
import { isValid } from 'date-fns';

export interface UTCDateRange {
  startUTC: Date;
  endUTC: Date;
  sourceTimezone: string;
  purpose: 'slot_generation' | 'holiday_blocking' | 'calendar_sync' | 'display';
}

export interface TimezoneContext {
  user?: { timezone: string; profileId?: number };
  display: { timezone: string };
  participants?: Array<{ timezone: string; userId: number }>;
  meeting?: { hostTimezone: string; type: 'individual' | 'group' | 'round_robin' };
}

/**
 * Client-side Centralized Timezone Service - Single Source of Truth
 *
 * This mirrors the server-side TimezoneService for consistent behavior
 * across frontend and backend.
 */
export class TimezoneService {

  /**
   * Convert local date/time in specific timezone to UTC for API transmission
   */
  static toUTC(localDateTime: Date, timezone: string): Date {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    if (!isValid(localDateTime)) {
      throw new Error(`Invalid date: ${localDateTime}`);
    }

    return zonedTimeToUtc(localDateTime, timezone);
  }

  /**
   * Convert UTC date to local time in specific timezone for display
   */
  static fromUTC(utcDateTime: Date, timezone: string): Date {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    if (!isValid(utcDateTime)) {
      throw new Error(`Invalid UTC date: ${utcDateTime}`);
    }

    return utcToZonedTime(utcDateTime, timezone);
  }

  /**
   * CRITICAL: Create proper date range for clicked calendar date
   * This fixes "Sept 20 click shows Sept 19 slots" issue
   *
   * @param clickedDate The Date object from calendar click
   * @param timezone The display timezone
   * @returns Object with startDate and endDate strings for API
   */
  static createAPIDateRangeFromClick(
    clickedDate: Date,
    timezone: string
  ): { startDate: string; endDate: string } {

    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    // Get the calendar date components from clicked date
    // This preserves the user's intended calendar date
    const year = clickedDate.getFullYear();
    const month = clickedDate.getMonth();
    const day = clickedDate.getDate();

    // Create the date string in YYYY-MM-DD format
    // This represents the calendar date the user clicked
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // For single-day requests, both start and end date are the same
    // The backend will handle creating the proper UTC time ranges
    return {
      startDate: dateString,
      endDate: dateString
    };
  }

  /**
   * Create month date range for API (used for prefetching month data)
   */
  static createAPIMonthRange(
    year: number,
    month: number, // 1-based
    timezone: string
  ): { startDate: string; endDate: string } {

    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    // Create month boundaries as date strings
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;

    // Get last day of month
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth).padStart(2, '0')}`;

    return { startDate, endDate };
  }

  /**
   * Format date for display in specific timezone
   */
  static formatForDisplay(date: Date, timezone: string, formatString: string = 'yyyy-MM-dd'): string {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    const localDate = this.fromUTC(date, timezone);
    return format(localDate, formatString, { timeZone: timezone });
  }

  /**
   * Get cache key for date in specific timezone
   * This ensures consistent cache keys across timezone switches
   */
  static getCacheKey(date: Date, timezone: string): string {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    // Convert date to timezone-aware date, then format
    const localDate = this.fromUTC(date, timezone);
    return format(localDate, 'yyyy-MM-dd');
  }

  /**
   * Check if date is "today" in specific timezone
   * Fixes "today" marker timezone issues
   */
  static isToday(date: Date, timezone: string): boolean {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    const now = this.now(timezone);
    const checkDate = this.fromUTC(date, timezone);

    return (
      now.getFullYear() === checkDate.getFullYear() &&
      now.getMonth() === checkDate.getMonth() &&
      now.getDate() === checkDate.getDate()
    );
  }

  /**
   * Get current time in specific timezone
   */
  static now(timezone: string): Date {
    if (!this.validateTimezone(timezone)) {
      throw new Error(`Invalid timezone: ${timezone}`);
    }

    return this.fromUTC(new Date(), timezone);
  }

  /**
   * Validate timezone string
   */
  static validateTimezone(timezone: string): boolean {
    try {
      new Intl.DateTimeFormat('en', { timeZone: timezone }).format(new Date());
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Debug helper for frontend timezone operations
   */
  static debugConversion(
    operation: string,
    input: Date | string,
    timezone: string,
    output: Date | string
  ): void {
    // No-op: debug logging removed
  }

  /**
   * Create timezone context for client-side operations
   */
  static createTimezoneContext(
    displayTimezone: string,
    userTimezone?: string
  ): TimezoneContext {
    return {
      display: { timezone: displayTimezone },
      user: userTimezone ? { timezone: userTimezone } : undefined
    };
  }

}

export default TimezoneService;